import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TagIcon from '@mui/icons-material/LocalOffer';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { Project } from 'api';
import dayjs, { type Dayjs } from 'dayjs';
import { ChevronDownSquare, ChevronUpSquare, MoreHorizontalIcon } from 'lucide-react';
import { useSnackbar } from 'notistack';
import React, { useMemo, useRef, useState } from 'react';
import { formatDuration } from 'shared/lib/formatDuration';

import type { TimeEntry } from '../../../api/types/timeEntry.types';
import type { UpdateTimeEntryDto } from '../../../api/types/timeEntry.types';
import {
  BillableChip,
  ChildEntriesContainer,
  ClientGroupHeader,
  ClientGroupLabel,
  DatePickerWrapper,
  DayGroupCard,
  DayGroupCheckbox,
  DayGroupHeader,
  DayLabel,
  DayTotal,
  DurationInput,
  EntryActions,
  EntryDescription,
  EntryDescriptionInput,
  EntryDuration,
  EntryIconButton,
  EntryPlaceholder,
  EntryProjectBadge,
  EntryRow,
  EntryRowCheckbox,
  EntryTimeRange,
  GroupCountBadge,
  SessionCountBadge,
  MenuItemDeleteIcon,
  MenuItemDuplicateIcon,
  NextDayBadge,
  PlayIconButton,
  ProjectColorDot,
  ProjectPickerItem,
  ProjectPickerItemContent,
  ProjectPickerItemName,
  ProjectPickerList,
  ProjectPickerPopover,
  ProjectPickerSearch,
  TimeEditContainer,
  TimeInput,
  TimeSeparator,
  MENU_PAPER_SX,
  MENU_TEXT_SX,
  MENU_ITEM_SX,
} from '../tracker.styles';
import type { DayGroup } from '../types/tracker.types';
import {
  formatDayLabel,
  parseDurationToSecs,
  parseTimeDigits,
  toHHMM,
} from '../utils/trackerUtils';

// ─── Similar-entry grouping ───────────────────────────────────────────────────

interface FlatItem {
  entry: TimeEntry;
  projectName?: string;
  projectColor?: string;
}

type DisplayGroup = { kind: 'single'; item: FlatItem } | { kind: 'grouped'; items: FlatItem[] };

const similarKey = (e: TimeEntry) =>
  `${e.projectId ?? ''}|${(e.description ?? '').trim()}|${e.isBillable}`;

const groupSimilarForDisplay = (flatItems: FlatItem[]): DisplayGroup[] => {
  const buckets = new Map<string, FlatItem[]>();
  for (const item of flatItems) {
    const k = similarKey(item.entry);
    const arr = buckets.get(k) ?? [];
    arr.push(item);
    buckets.set(k, arr);
  }

  const seen = new Set<string>();
  const result: DisplayGroup[] = [];
  for (const item of flatItems) {
    const k = similarKey(item.entry);
    if (seen.has(k)) continue;
    seen.add(k);
    const group = buckets.get(k)!;
    if (group.length === 1) {
      result.push({ kind: 'single', item: group[0] });
    } else {
      result.push({ kind: 'grouped', items: group });
    }
  }
  return result;
};

// ─── EntryRowItem ─────────────────────────────────────────────────────────────

interface EntryRowItemProps {
  entry: TimeEntry;
  projectName?: string;
  projectColor?: string;
  projects: Project[];
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (entry: TimeEntry) => void;
  onUpdate: (id: string, data: UpdateTimeEntryDto) => void;
  onContinue: (entry: TimeEntry) => void;
}

const EntryRowItem: React.FC<EntryRowItemProps> = ({
  entry,
  projectName,
  projectColor,
  projects,
  isSelected,
  onToggleSelect,
  onDelete,
  onDuplicate,
  onUpdate,
  onContinue,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(null);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [collapsedClients, setCollapsedClients] = useState<Set<string>>(new Set());
  const projectBadgeRef = useRef<HTMLDivElement>(null);

  const filteredProjects = useMemo(() => {
    const q = projectSearch.toLowerCase();
    return q
      ? projects.filter(
          (p) => p.name.toLowerCase().includes(q) || (p.clientName ?? '').toLowerCase().includes(q),
        )
      : projects;
  }, [projects, projectSearch]);

  const projectGroups = useMemo(() => {
    const map = new Map<string, Project[]>();
    for (const p of filteredProjects) {
      const key = p.clientName ?? 'No Client';
      const arr = map.get(key) ?? [];
      arr.push(p);
      map.set(key, arr);
    }
    return Array.from(map.entries()).map(([clientName, ps]) => ({ clientName, projects: ps }));
  }, [filteredProjects]);

  const handleProjectSelect = (p: Project) => {
    setProjectPickerOpen(false);
    setProjectSearch('');
    onUpdate(entry.id, {
      projectId: p.id,
      description: entry.description,
      startedAt: entry.startedAt,
      endedAt: entry.endedAt ?? undefined,
      isBillable: p.isBillable,
      tagIds: entry.tagIds,
    });
  };

  const [draftDesc, setDraftDesc] = useState(entry.description ?? '');
  const [draftStart, setDraftStart] = useState(
    entry.startedAt ? toHHMM(entry.startedAt).replace(':', '') : '',
  );
  const [draftEnd, setDraftEnd] = useState(
    entry.endedAt ? toHHMM(entry.endedAt).replace(':', '') : '',
  );
  const [draftDate, setDraftDate] = useState<Dayjs>(dayjs(entry.startedAt));
  const [editingDesc, setEditingDesc] = useState(false);
  const [editingTime, setEditingTime] = useState(false);
  const [editingDuration, setEditingDuration] = useState(false);
  const [draftDuration, setDraftDuration] = useState('');
  const [localBillable, setLocalBillable] = useState(entry.isBillable);

  React.useEffect(() => {
    setLocalBillable(entry.isBillable);
  }, [entry.isBillable]);

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    if (!editingDesc) setDraftDesc(entry.description ?? '');
  }, [entry.description]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildUpdate = (
    overrides?: Partial<{ desc: string; start: string; end: string; date: Dayjs }>,
  ): UpdateTimeEntryDto => {
    const desc = overrides?.desc ?? draftDesc;
    const timeChanged =
      overrides?.start !== undefined ||
      overrides?.end !== undefined ||
      overrides?.date !== undefined;

    if (!timeChanged) {
      return {
        projectId: entry.projectId ?? '',
        description: desc.trim() || undefined,
        startedAt: entry.startedAt,
        endedAt: entry.endedAt,
        isBillable: entry.isBillable,
        tagIds: entry.tagIds,
      };
    }

    const start =
      overrides?.start ??
      parseTimeDigits(draftStart) ??
      (entry.startedAt ? toHHMM(entry.startedAt) : '00:00');
    const end =
      overrides?.end ?? parseTimeDigits(draftEnd) ?? (entry.endedAt ? toHHMM(entry.endedAt) : '');
    const date = overrides?.date ?? draftDate;
    const dateStr = date.format('YYYY-MM-DD');

    const startDate = new Date(`${dateStr}T${start}:00`);
    let endedAt: string | undefined;
    if (end) {
      const endDate = new Date(`${dateStr}T${end}:00`);
      if (endDate <= startDate) {
        endDate.setDate(endDate.getDate() + 1);
      }
      endedAt = endDate.toISOString();
    }

    return {
      projectId: entry.projectId ?? '',
      description: desc.trim() || undefined,
      startedAt: startDate.toISOString(),
      endedAt,
      isBillable: entry.isBillable,
      tagIds: entry.tagIds,
    };
  };

  const scheduleSave = (overrides?: Parameters<typeof buildUpdate>[0]) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      const desc = overrides?.desc ?? draftDesc;
      const start =
        overrides?.start ??
        parseTimeDigits(draftStart) ??
        (entry.startedAt ? toHHMM(entry.startedAt) : '');
      const end =
        overrides?.end ?? parseTimeDigits(draftEnd) ?? (entry.endedAt ? toHHMM(entry.endedAt) : '');
      const date = overrides?.date ?? draftDate;

      const descSame = (entry.description ?? '').trim() === desc.trim();
      const startSame = start === (entry.startedAt ? toHHMM(entry.startedAt) : '');
      const endSame = end === (entry.endedAt ? toHHMM(entry.endedAt) : '');
      const dateSame = date.format('YYYY-MM-DD') === dayjs(entry.startedAt).format('YYYY-MM-DD');

      if (descSame && startSame && endSame && dateSame) return;
      onUpdate(entry.id, buildUpdate(overrides));
    }, 300);
  };

  const commitTimeEdit = () => {
    setEditingTime(false);

    const originalStart = entry.startedAt ? toHHMM(entry.startedAt) : '00:00';
    const originalEnd = entry.endedAt ? toHHMM(entry.endedAt) : '';

    const parsedStart = parseTimeDigits(draftStart);
    const parsedEnd = parseTimeDigits(draftEnd);

    // Show error toast for non-empty invalid input, then revert to original
    if (parsedStart === null && draftStart.trim() !== '')
      enqueueSnackbar('Invalid start time', { variant: 'warning' });
    if (parsedEnd === null && draftEnd.trim() !== '')
      enqueueSnackbar('Invalid end time', { variant: 'warning' });

    if (parsedStart === null) setDraftStart(originalStart.replace(':', ''));
    if (parsedEnd === null) setDraftEnd(originalEnd.replace(':', ''));

    const resolvedStart = parsedStart ?? originalStart;
    const resolvedEnd = parsedEnd ?? originalEnd;

    scheduleSave({ start: resolvedStart, end: resolvedEnd });
  };

  const cancelTimeEdit = () => {
    setDraftStart(entry.startedAt ? toHHMM(entry.startedAt).replace(':', '') : '');
    setDraftEnd(entry.endedAt ? toHHMM(entry.endedAt).replace(':', '') : '');
    setEditingTime(false);
  };

  const commitDurationEdit = () => {
    setEditingDuration(false);
    const secs = parseDurationToSecs(draftDuration);
    if (secs === null || secs <= 0) {
      if (draftDuration.trim()) enqueueSnackbar('Invalid duration', { variant: 'warning' });
      return;
    }
    const newEndedAt = new Date(new Date(entry.startedAt).getTime() + secs * 1000).toISOString();
    onUpdate(entry.id, {
      projectId: entry.projectId ?? '',
      description: entry.description,
      startedAt: entry.startedAt,
      endedAt: newEndedAt,
      isBillable: entry.isBillable,
      tagIds: entry.tagIds,
    });
  };

  const duration = entry.durationSecs != null ? formatDuration(entry.durationSecs) : '—';
  const displayTimeRange =
    entry.startedAt && entry.endedAt
      ? `${toHHMM(entry.startedAt)} — ${toHHMM(entry.endedAt)}`
      : entry.startedAt
        ? `${toHHMM(entry.startedAt)} —`
        : '—';
  const dayOverflow =
    entry.startedAt && entry.endedAt
      ? Math.round(
          dayjs(entry.endedAt)
            .startOf('day')
            .diff(dayjs(entry.startedAt).startOf('day'), 'day', true),
        )
      : 0;

  return (
    <EntryRow>
      <EntryRowCheckbox
        size="small"
        checked={isSelected}
        onChange={() => onToggleSelect(entry.id)}
      />

      {editingDesc ? (
        <EntryDescriptionInput
          autoFocus
          value={draftDesc}
          onChange={(e) => setDraftDesc(e.target.value)}
          onBlur={() => {
            setEditingDesc(false);
            scheduleSave({ desc: draftDesc });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setEditingDesc(false);
              scheduleSave({ desc: draftDesc });
            }
            if (e.key === 'Escape') {
              setDraftDesc(entry.description ?? '');
              setEditingDesc(false);
            }
          }}
          placeholder="Add description"
        />
      ) : (
        <Tooltip title={entry.description || ''} placement="bottom-start">
          <EntryDescription onClick={() => setEditingDesc(true)}>
            {entry.description || <EntryPlaceholder>(no description)</EntryPlaceholder>}
          </EntryDescription>
        </Tooltip>
      )}

      <ClickAwayListener onClickAway={() => setProjectPickerOpen(false)}>
        <div>
          <EntryProjectBadge
            ref={projectBadgeRef}
            dotcolor={projectColor}
            onClick={() => setProjectPickerOpen((o) => !o)}
          >
            <span className="dot" />
            <span className="name">{projectName ?? 'No project'}</span>
            {projectPickerOpen ? (
              <ExpandLessIcon className="chevron" />
            ) : (
              <ExpandMoreIcon className="chevron" />
            )}
          </EntryProjectBadge>

          <Popper
            open={projectPickerOpen}
            anchorEl={projectBadgeRef.current}
            placement="bottom-start"
            style={{ zIndex: 1400 }}
          >
            <ProjectPickerPopover>
              <ProjectPickerSearch
                autoFocus
                placeholder="Find project or client"
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
              />
              <ProjectPickerList>
                {projectGroups.map((group) => {
                  const collapsed = collapsedClients.has(group.clientName);
                  return (
                    <div key={group.clientName}>
                      <ClientGroupHeader
                        onClick={() =>
                          setCollapsedClients((prev) => {
                            const next = new Set(prev);
                            if (next.has(group.clientName)) next.delete(group.clientName);
                            else next.add(group.clientName);
                            return next;
                          })
                        }
                      >
                        <ClientGroupLabel>{group.clientName}</ClientGroupLabel>
                        <ClientGroupLabel style={{ fontWeight: 400 }}>
                          {group.projects.length} project{group.projects.length !== 1 ? 's' : ''}
                        </ClientGroupLabel>
                        {collapsed ? (
                          <ExpandMoreIcon style={{ fontSize: 16 }} />
                        ) : (
                          <ExpandLessIcon style={{ fontSize: 16 }} />
                        )}
                      </ClientGroupHeader>
                      {!collapsed &&
                        group.projects.map((p) => (
                          <ProjectPickerItem
                            key={p.id}
                            selected={p.id === entry.projectId ? 1 : 0}
                            onClick={() => handleProjectSelect(p)}
                          >
                            <ProjectPickerItemContent>
                              <ProjectColorDot dotcolor={p.color} />
                              <ProjectPickerItemName>{p.name}</ProjectPickerItemName>
                            </ProjectPickerItemContent>
                          </ProjectPickerItem>
                        ))}
                    </div>
                  );
                })}
                {projectGroups.length === 0 && (
                  <ProjectPickerItem>
                    <ProjectPickerItemName>No projects found</ProjectPickerItemName>
                  </ProjectPickerItem>
                )}
              </ProjectPickerList>
            </ProjectPickerPopover>
          </Popper>
        </div>
      </ClickAwayListener>

      <EntryActions>
        <Tooltip title={localBillable ? 'Mark as non-billable' : 'Mark as billable'}>
          <BillableChip
            active={localBillable ? 1 : 0}
            isClickable={1}
            onClick={() => {
              const next = !localBillable;
              setLocalBillable(next);
              onUpdate(entry.id, {
                projectId: entry.projectId ?? '',
                description: entry.description,
                startedAt: entry.startedAt,
                endedAt: entry.endedAt,
                isBillable: next,
                tagIds: entry.tagIds,
              });
            }}
          >
            <AttachMoneyIcon />
            {localBillable ? 'Billable' : 'Non-billable'}
          </BillableChip>
        </Tooltip>

        {entry.tagIds.length > 0 && (
          <Tooltip title="Has tags">
            <TagIcon style={{ fontSize: 16 }} />
          </Tooltip>
        )}

        <Tooltip title="Change date">
          <EntryIconButton size="small" onClick={(e) => setCalendarAnchor(e.currentTarget)}>
            <CalendarTodayIcon style={{ fontSize: 14 }} />
          </EntryIconButton>
        </Tooltip>

        <Popover
          open={Boolean(calendarAnchor)}
          anchorEl={calendarAnchor}
          onClose={() => setCalendarAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              value={draftDate}
              onChange={(newDate) => {
                if (newDate) {
                  setDraftDate(newDate);
                  setCalendarAnchor(null);
                  scheduleSave({ date: newDate });
                }
              }}
            />
          </LocalizationProvider>
        </Popover>

        {editingTime ? (
          <TimeEditContainer
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) commitTimeEdit();
            }}
          >
            <TimeInput
              autoFocus
              value={draftStart}
              onChange={(e) => setDraftStart(e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitTimeEdit();
                if (e.key === 'Escape') cancelTimeEdit();
              }}
              inputProps={{ maxLength: 5 }}
            />
            <TimeSeparator>—</TimeSeparator>
            <TimeInput
              value={draftEnd}
              onChange={(e) => setDraftEnd(e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitTimeEdit();
                if (e.key === 'Escape') cancelTimeEdit();
              }}
              inputProps={{ maxLength: 5 }}
            />
          </TimeEditContainer>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Tooltip title="Click to edit time">
              <EntryTimeRange
                onClick={() => {
                  setDraftStart(entry.startedAt ? toHHMM(entry.startedAt).replace(':', '') : '');
                  setDraftEnd(entry.endedAt ? toHHMM(entry.endedAt).replace(':', '') : '');
                  setEditingTime(true);
                }}
              >
                {displayTimeRange}
              </EntryTimeRange>
            </Tooltip>
            {/* {dayOverflow > 0 && <NextDayBadge>+{dayOverflow}</NextDayBadge>} */}
          </Box>
        )}

        {editingDuration ? (
          <DurationInput
            autoFocus
            value={draftDuration}
            onChange={(e) => setDraftDuration(e.target.value)}
            onFocus={(e) => e.target.select()}
            onBlur={commitDurationEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitDurationEdit();
              if (e.key === 'Escape') {
                setDraftDuration(duration);
                setEditingDuration(false);
              }
            }}
            inputProps={{ maxLength: 8 }}
          />
        ) : (
          <Tooltip title="Click to edit duration">
            <EntryDuration
              iseditable={entry.endedAt ? 1 : 0}
              onClick={() => {
                if (!entry.endedAt) return;
                setDraftDuration(duration);
                setEditingDuration(true);
              }}
            >
              {duration}
            </EntryDuration>
          </Tooltip>
        )}

        <Tooltip title="Continue">
          <PlayIconButton
            size="small"
            className="entry-hover-icon"
            onClick={() => onContinue(entry)}
          >
            <PlayArrowIcon fontSize="small" />
          </PlayIconButton>
        </Tooltip>

        <EntryIconButton
          size="small"
          className="entry-hover-icon"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <MoreVertIcon fontSize="small" />
        </EntryIconButton>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: MENU_PAPER_SX } }}
        >
          <MenuItem
            sx={MENU_ITEM_SX}
            onClick={() => {
              setMenuAnchor(null);
              onDuplicate(entry);
            }}
          >
            <MenuItemDuplicateIcon />
            <ListItemText primaryTypographyProps={{ sx: MENU_TEXT_SX }}>Duplicate</ListItemText>
          </MenuItem>
          <MenuItem
            sx={MENU_ITEM_SX}
            onClick={() => {
              setMenuAnchor(null);
              onDelete(entry.id);
            }}
          >
            <MenuItemDeleteIcon />
            <ListItemText primaryTypographyProps={{ sx: MENU_TEXT_SX }}>Delete</ListItemText>
          </MenuItem>
        </Menu>
      </EntryActions>
    </EntryRow>
  );
};

// ─── GroupedEntryRow ──────────────────────────────────────────────────────────

interface GroupedEntryRowProps {
  items: FlatItem[];
  projects: Project[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (entry: TimeEntry) => void;
  onUpdate: (id: string, data: UpdateTimeEntryDto) => void;
  onUpdateBatch: (updates: Array<{ id: string; data: UpdateTimeEntryDto }>) => Promise<void>;
  onContinue: (entry: TimeEntry) => void;
}

const GroupedEntryRow: React.FC<GroupedEntryRowProps> = ({
  items,
  projects,
  selectedIds,
  onToggleSelect,
  onDelete,
  onDuplicate,
  onUpdate,
  onUpdateBatch,
  onContinue,
}) => {
  const first = items[0];

  const [expanded, setExpanded] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [draftDesc, setDraftDesc] = useState(first.entry.description ?? '');
  const [localBillable, setLocalBillable] = useState(first.entry.isBillable);

  React.useEffect(() => {
    if (!editingDesc) setDraftDesc(first.entry.description ?? '');
  }, [first.entry.description]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setLocalBillable(first.entry.isBillable);
  }, [first.entry.isBillable]);

  const entries = items.map((i) => i.entry);

  const minStart = entries.reduce(
    (min, e) => (e.startedAt < min ? e.startedAt : min),
    entries[0].startedAt,
  );
  const maxEnd = entries.reduce<string | undefined>((max, e) => {
    if (!e.endedAt) return max;
    return !max || e.endedAt > max ? e.endedAt : max;
  }, undefined);
  const totalSecs = entries.reduce((sum, e) => sum + (e.durationSecs ?? 0), 0);
  const hasAnyTags = entries.some((e) => e.tagIds.length > 0);

  const allSelected = entries.every((e) => selectedIds.has(e.id));
  const someSelected = entries.some((e) => selectedIds.has(e.id));

  const handleToggleAll = () => {
    if (allSelected) {
      entries.forEach((e) => {
        if (selectedIds.has(e.id)) onToggleSelect(e.id);
      });
    } else {
      entries.forEach((e) => {
        if (!selectedIds.has(e.id)) onToggleSelect(e.id);
      });
    }
  };

  const saveDescription = (desc: string) => {
    if (desc.trim() === (first.entry.description ?? '').trim()) return;
    onUpdateBatch(
      items.map(({ entry }) => ({
        id: entry.id,
        data: {
          projectId: entry.projectId ?? '',
          description: desc.trim() || undefined,
          startedAt: entry.startedAt,
          endedAt: entry.endedAt,
          isBillable: entry.isBillable,
          tagIds: entry.tagIds,
        },
      })),
    );
  };

  const toggleBillable = () => {
    const next = !localBillable;
    setLocalBillable(next);
    onUpdateBatch(
      items.map(({ entry }) => ({
        id: entry.id,
        data: {
          projectId: entry.projectId ?? '',
          description: entry.description,
          startedAt: entry.startedAt,
          endedAt: entry.endedAt,
          isBillable: next,
          tagIds: entry.tagIds,
        },
      })),
    );
  };

  return (
    <>
      <EntryRow>
        <EntryRowCheckbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={handleToggleAll}
        />

        <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
          <GroupCountBadge onClick={() => setExpanded((v) => !v)}>{entries.length}</GroupCountBadge>
        </Tooltip>

        {editingDesc ? (
          <EntryDescriptionInput
            autoFocus
            value={draftDesc}
            onChange={(e) => setDraftDesc(e.target.value)}
            onBlur={() => {
              setEditingDesc(false);
              saveDescription(draftDesc);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setEditingDesc(false);
                saveDescription(draftDesc);
              }
              if (e.key === 'Escape') {
                setDraftDesc(first.entry.description ?? '');
                setEditingDesc(false);
              }
            }}
            placeholder="Add description"
          />
        ) : (
          <Tooltip title={first.entry.description || ''} placement="bottom-start">
            <EntryDescription onClick={() => setEditingDesc(true)}>
              {first.entry.description || <EntryPlaceholder>(no description)</EntryPlaceholder>}
            </EntryDescription>
          </Tooltip>
        )}

        <EntryProjectBadge dotcolor={first.projectColor} isReadOnly={1}>
          <span className="dot" />
          <span className="name">{first.projectName ?? 'No project'}</span>
        </EntryProjectBadge>

        <EntryActions>
          <Tooltip title={localBillable ? 'Mark as non-billable' : 'Mark as billable'}>
            <BillableChip active={localBillable ? 1 : 0} isClickable={1} onClick={toggleBillable}>
              <AttachMoneyIcon />
              {localBillable ? 'Billable' : 'Non-billable'}
            </BillableChip>
          </Tooltip>

          {hasAnyTags && (
            <Tooltip title="Has tags">
              <TagIcon style={{ fontSize: 16 }} />
            </Tooltip>
          )}

          <DatePickerWrapper>
            <EntryTimeRange isReadOnly={1}>
              {toHHMM(minStart)} — {maxEnd ? toHHMM(maxEnd) : ''}
            </EntryTimeRange>
          </DatePickerWrapper>
          <EntryDuration>{formatDuration(totalSecs)}</EntryDuration>

          <Tooltip title="Continue">
            <PlayIconButton
              size="small"
              className="entry-hover-icon"
              onClick={() => onContinue(items[0].entry)}
            >
              <PlayArrowIcon fontSize="small" />
            </PlayIconButton>
          </Tooltip>

          <EntryIconButton size="small" onClick={() => setExpanded((v) => !v)}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </EntryIconButton>
        </EntryActions>
      </EntryRow>

      {expanded && (
        <ChildEntriesContainer>
          {items
            .slice()
            .sort((a, b) => (a.entry.startedAt > b.entry.startedAt ? -1 : 1))
            .map(({ entry, projectName, projectColor }) => (
              <EntryRowItem
                key={entry.id}
                entry={entry}
                projectName={projectName}
                projectColor={projectColor}
                projects={projects}
                isSelected={selectedIds.has(entry.id)}
                onToggleSelect={onToggleSelect}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
                onUpdate={onUpdate}
                onContinue={onContinue}
              />
            ))}
        </ChildEntriesContainer>
      )}
    </>
  );
};

// ─── TimeEntryDayGroup ────────────────────────────────────────────────────────

interface TimeEntryDayGroupProps {
  group: DayGroup;
  projects: Project[];
  onDelete: (id: string) => void;
  onDuplicate: (entry: TimeEntry) => void;
  onUpdate: (id: string, data: UpdateTimeEntryDto) => void;
  onUpdateBatch: (updates: Array<{ id: string; data: UpdateTimeEntryDto }>) => Promise<void>;
  onContinue: (entry: TimeEntry) => void;
}

export const TimeEntryDayGroup: React.FC<TimeEntryDayGroupProps> = ({
  group,
  projects,
  onDelete,
  onDuplicate,
  onUpdate,
  onUpdateBatch,
  onContinue,
}) => {
  const allIds = group.projectGroups.flatMap((pg) => pg.entries.map((e) => e.id));
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [groupMenuAnchor, setGroupMenuAnchor] = useState<HTMLElement | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const someSelected = allIds.some((id) => selectedIds.has(id));

  const handleHeaderCheckbox = () => {
    setSelectedIds(allSelected ? new Set() : new Set(allIds));
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDeleteSelected = () => {
    setGroupMenuAnchor(null);
    selectedIds.forEach((id) => onDelete(id));
    setSelectedIds(new Set());
  };

  const flatItems: FlatItem[] = group.projectGroups
    .flatMap((pg) =>
      pg.entries.map((entry) => ({
        entry,
        projectName: pg.projectName,
        projectColor: pg.projectColor,
      })),
    )
    .sort((a, b) => (a.entry.startedAt > b.entry.startedAt ? -1 : 1));

  const displayGroups = groupSimilarForDisplay(flatItems);

  return (
    <DayGroupCard>
      <DayGroupHeader>
        <DayGroupCheckbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={handleHeaderCheckbox}
        />
        <IconButton size="small" onClick={() => setCollapsed((v) => !v)} sx={{ mr: 0.5, p: 0.25 }}>
          {collapsed ? (
            <ChevronUpSquare size={16} color="#C4BFDA" strokeWidth={1.5} fill="#FFFFFF" />
          ) : (
            <ChevronDownSquare size={16} color="#C4BFDA" strokeWidth={1.5} fill="#FFFFFF" />
          )}
        </IconButton>
        <DayLabel>{formatDayLabel(group.date)}</DayLabel>
        <SessionCountBadge>
          {flatItems.length} session{flatItems.length !== 1 ? 's' : ''}
        </SessionCountBadge>
        <DayTotal>{formatDuration(group.totalSecs)}</DayTotal>
        <IconButton size="small" onClick={(e) => setGroupMenuAnchor(e.currentTarget)}>
          <MoreHorizontalIcon size={16} />
        </IconButton>
        <Menu
          anchorEl={groupMenuAnchor}
          open={Boolean(groupMenuAnchor)}
          onClose={() => setGroupMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: MENU_PAPER_SX } }}
        >
          <MenuItem
            disabled={!someSelected}
            onClick={handleDeleteSelected}
            sx={{ ...MENU_ITEM_SX, color: 'error.main' }}
          >
            <MenuItemDeleteIcon />
            <ListItemText primaryTypographyProps={{ sx: MENU_TEXT_SX }}>
              Delete selected {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
            </ListItemText>
          </MenuItem>
        </Menu>
      </DayGroupHeader>

      {!collapsed &&
        displayGroups.map((dg) =>
          dg.kind === 'single' ? (
            <EntryRowItem
              key={dg.item.entry.id}
              entry={dg.item.entry}
              projectName={dg.item.projectName}
              projectColor={dg.item.projectColor}
              projects={projects}
              isSelected={selectedIds.has(dg.item.entry.id)}
              onToggleSelect={handleToggleSelect}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onUpdate={onUpdate}
              onContinue={onContinue}
            />
          ) : (
            <GroupedEntryRow
              key={dg.items[0].entry.id}
              items={dg.items}
              projects={projects}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onUpdate={onUpdate}
              onContinue={onContinue}
              onUpdateBatch={onUpdateBatch}
            />
          ),
        )}
    </DayGroupCard>
  );
};
